<template>
  <div class="cms-gallery-upload" :class="{ 'cms-form-group-full': fullWidth }">
    <label v-if="label" class="cms-gallery-upload-label">{{ label }}</label>
    <p v-if="helpText" class="cms-form-help">{{ helpText }}</p>
    
    <div class="cms-gallery-grid" v-if="modelValue && modelValue.length > 0">
      <div v-for="(image, index) in modelValue" :key="index" class="cms-gallery-item">
        <img :src="image" :alt="`Gallery image ${index + 1}`" />
        <button type="button" class="cms-btn-remove" @click="removeImage(index)">
          <v-icon icon="trash" size="sm" />
        </button>
      </div>
    </div>
    
    <div class="cms-gallery-upload-options">
      <div class="cms-gallery-uploader">
        <v-uploader
          ref="uploader"
          :initial-value="[]"
          :multiple="true"
          :max-items="maxItems"
          :route-prefix="routePrefix"
          :enable-library="false"
          :after-upload="handleUpload"
            @removed="handleRemove"
        />
      </div>
      <div class="cms-gallery-upload-divider">
        <span>{{ $t('cms.media.or') }}</span>
      </div>
      <button type="button" class="cms-btn cms-btn-library" @click="openMediaPicker">
        <v-icon icon="picture" size="md" />
        {{ $t('cms.media.chooseFromLibrary') }}
      </button>
    </div>

    <!-- Media Picker -->
    <cms-media-picker
      v-model="showMediaPicker"
      filter-type="image"
      :multiple="true"
      :max-selection="maxItems"
      @selected="handleMediaSelected"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, defineExpose, watch, onMounted } from 'vue';
import VUploader from '@/components/vendor/upload/VUploader.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsMediaPicker from '@/components/cms/CmsMediaPicker.vue';
import { extractMediaUrl } from '@/helpers/cmsMediaUpload';

interface Props {
  modelValue?: string[];
  label?: string;
  helpText?: string;
  fullWidth?: boolean;
  routePrefix?: string;
  maxItems?: number;
  initialMedia?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  fullWidth: false,
  routePrefix: 'api/s3',
  maxItems: 20,
  initialMedia: () => [],
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
  (e: 'uploaded', media: any[]): void;
  (e: 'removed', media: any[]): void;
}>();

const uploader = ref<any>(null);
const mediaItems = ref<any[]>([]);
const pendingInitialMedia = ref<any[]>([]);
const showMediaPicker = ref(false);

const updateMediaState = () => {
  if (uploader.value?.mediaLibrary) {
    mediaItems.value = [...uploader.value.mediaLibrary.state.media];
  }
};

const handleUpload = () => {
  updateMediaState();
  const newImages = mediaItems.value
    .map((item: any) => extractMediaUrl(item))
    .filter((url: string) => !!url);
  if (newImages.length > 0) {
    const updatedGallery = [...(props.modelValue || []), ...newImages.slice(-1)];
    emit('update:modelValue', updatedGallery);
  }
  emit('uploaded', [...mediaItems.value]);
};

const handleRemove = () => {
  updateMediaState();
  emit('removed', [...mediaItems.value]);
};

const removeImage = (index: number) => {
  const currentGallery = [...(props.modelValue || [])];
  const removedUrl = currentGallery[index];
  currentGallery.splice(index, 1);
  emit('update:modelValue', currentGallery);

  const mediaState = uploader.value?.mediaLibrary?.state.media || [];
  const mediaIndex = mediaState.findIndex((item: any) => extractMediaUrl(item) === removedUrl);

  if (mediaIndex !== -1) {
    const mediaItem = mediaState[mediaIndex];
    if (mediaItem && uploader.value?.remove) {
      uploader.value.remove(mediaItem);
    } else if (uploader.value?.mediaLibrary) {
      uploader.value.mediaLibrary.state.media = mediaState.filter((_: any, idx: number) => idx !== mediaIndex);
    }
  }

  mediaItems.value = mediaItems.value.filter((item: any) => extractMediaUrl(item) !== removedUrl);
  emit('removed', [...mediaItems.value]);
};

const clearMedia = () => {
  mediaItems.value = [];
  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = [];
  }
};

const setMediaItems = (items: any[] = [], { force = false }: { force?: boolean } = {}) => {
  const normalized = Array.isArray(items) ? items.map((item: any) => JSON.parse(JSON.stringify(item))) : [];
  mediaItems.value = normalized;

  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = normalized.map((item: any) => ({ ...item }));
  } else if (!force) {
    pendingInitialMedia.value = normalized;
  }
};

watch(
  () => props.initialMedia,
  (newItems) => {
    if (newItems && newItems.length > 0) {
      setMediaItems(newItems);
    } else {
      clearMedia();
    }
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  if (pendingInitialMedia.value.length > 0) {
    setMediaItems(pendingInitialMedia.value, { force: true });
    pendingInitialMedia.value = [];
  } else if (props.initialMedia?.length) {
    setMediaItems(props.initialMedia, { force: true });
  } else {
    updateMediaState();
  }
});

defineExpose({
  getMedia: () => mediaItems.value,
  clearMedia,
  setMedia: (items: any[] = []) => setMediaItems(items, { force: true }),
});

watch(() => props.modelValue, (newValue) => {
  if (!newValue || newValue.length === 0) {
    clearMedia();
  }
});

const openMediaPicker = () => {
  showMediaPicker.value = true;
};

const handleMediaSelected = (media: any | any[]) => {
  const mediaArray = Array.isArray(media) ? media : [media];
  const newUrls = mediaArray
    .map((item: any) => extractMediaUrl(item))
    .filter((url: string) => !!url);

  if (newUrls.length > 0) {
    mediaItems.value = [...mediaItems.value, ...mediaArray];
    const currentGallery = [...(props.modelValue || [])];
    const updatedGallery = [...currentGallery, ...newUrls];
    const finalGallery = props.maxItems
      ? updatedGallery.slice(0, props.maxItems)
      : updatedGallery;
    emit('update:modelValue', finalGallery);
    emit('uploaded', mediaArray);
  }
};
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-gallery-upload {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  &.cms-form-group-full {
    grid-column: 1 / -1;
  }
}

.cms-gallery-upload-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: $cms-text-primary;
}

.cms-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}

.cms-gallery-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid $cms-border-color;
  aspect-ratio: 1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .cms-btn-remove {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.25rem;
    cursor: pointer;
    transition: background 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 0, 0, 1);
    }
  }
}

.cms-gallery-upload-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cms-gallery-uploader {
  border: 2px dashed $cms-border-color;
  border-radius: 8px;
  padding: 1rem;
  min-height: 150px;
}

.cms-gallery-upload-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: $cms-text-secondary;
  font-size: 0.9rem;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid $cms-border-color;
  }
  
  span {
    padding: 0 1rem;
  }
}

.cms-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &-library {
    width: 100%;
    justify-content: center;
    background: $cms-primary-color;
    color: white;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    box-shadow: $cms-shadow-sm;
    
    i, svg, .icon {
      color: white;
    }

    &:hover {
      background: lighten($cms-primary-color, 10%);
      box-shadow: $cms-shadow-md;
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: $cms-shadow-sm;
    }
    
    :deep(.v-icon) {
      font-size: 1.25rem;
    }
  }
}
</style>

